import { useEffect, useState, type ReactNode } from "react";
import Button from "../../../shared/components/Button";
import { Box } from "../../../shared/components/Box";
import { Container } from "../../../shared/components/Container";
import { Grid } from "../../../shared/components/Grid";
import { Layout } from "../../../layouts/Layout";
import { Dropdown } from "../../../shared/components/form";
import "../pages/AlertsPage.scss";
import { acknowledgeWorkflowAlert, getWorkflowAlerts } from '../api';

type AlertSeverity = "Critical" | "Warning" | "Info";

type AlertItem = {
  id: string;
  severity: AlertSeverity;
  title: string;
  camera: string;
  detail: string;
  time: string;
  acknowledged: boolean;
};

const severityStyles: Record<AlertSeverity, string> = {
  Critical: "critical",
  Warning: "warning",
  Info: "info",
};

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      aria-hidden="true"
      className="icon"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      {children}
    </svg>
  );
}

function AlertSymbol({ severity }: { severity: AlertSeverity }) {
  if (severity === "Info")
    return (
      <Icon>
        <circle cx="12" cy="12" r="8" />
        <path strokeLinecap="round" d="M12 11v5M12 8h.01" />
      </Icon>
    );
  return (
    <Icon>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path strokeLinecap="round" d="M12 9v4M12 16.5h.01" />
    </Icon>
  );
}

export function AlertsDesktopView() {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [filter, setFilter] = useState<"all" | AlertSeverity>("all");
  const visibleAlerts = alerts.filter(
    (alert) => filter === "all" || alert.severity === filter,
  );
  useEffect(() => {
    getWorkflowAlerts().then((items) => setAlerts(items.map((item) => ({
      id: item.id,
      severity: item.severity === 'Critical' ? 'Critical' : item.severity === 'Info' ? 'Info' : 'Warning',
      title: `${item.type} detected`, camera: item.camera_id, detail: item.detail,
      time: new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      acknowledged: item.acknowledged,
    })))).catch(() => undefined);
  }, []);
  const acknowledge = async (id: string) => {
    await acknowledgeWorkflowAlert(id);
    setAlerts((current) => current.map((alert) => alert.id === id ? { ...alert, acknowledged: true } : alert));
  };

  return (
    <Layout activePath="/alerts" className="alerts-page">
      <Box as="main" className="main">
        <header className="header">
          <div>
            <p className="eyebrow">Saturday, 08 August 2026</p>
            <h1 className="title">Alerts</h1>
          </div>
          <span className="unacknowledged">
            {alerts.filter((alert) => !alert.acknowledged).length}{" "}
            unacknowledged
          </span>
        </header>
        <Container className="content" fluid>
          <Grid as="section" className="summary">
            {[
              [
                "Unacknowledged",
                alerts.filter((alert) => !alert.acknowledged).length,
                "danger",
              ],
              [
                "Critical",
                alerts.filter((alert) => alert.severity === "Critical").length,
                "danger",
              ],
              [
                "Resolved today",
                alerts.filter((alert) => alert.acknowledged).length,
                "live",
              ],
            ].map(([label, count, tone]) => (
              <div className="summary-card" key={label as string}>
                <p>{label}</p>
                <strong className={tone as string}>{count}</strong>
              </div>
            ))}
          </Grid>
          <section className="queue">
            <div className="queue-header">
              <div>
                <h2>Alert queue</h2>
                <p>Prioritized items requiring operator attention</p>
              </div>
              <Dropdown
                aria-label="Filter alerts by severity"
                value={filter}
                onChange={(event) =>
                  setFilter(event.target.value as "all" | AlertSeverity)
                }
              >
                <option value="all">All severities</option>
                <option value="Critical">Critical</option>
                <option value="Warning">Warning</option>
                <option value="Info">Info</option>
              </Dropdown>
            </div>
            <div className="alert-list">
              {visibleAlerts.length === 0 ? (
                <p className="empty">No alerts match filter.</p>
              ) : (
                visibleAlerts.map((alert) => {
                  return (
                    <article
                      className="alert-row"
                      key={alert.id}
                    >
                      <div
                        className={`alert-symbol ${severityStyles[alert.severity]}`}
                      >
                        <AlertSymbol severity={alert.severity} />
                      </div>
                      <div className="alert-copy">
                        <div>
                          <h3>{alert.title}</h3>
                          <span
                            className={`severity ${severityStyles[alert.severity]}`}
                          >
                            {alert.severity}
                          </span>
                          {alert.acknowledged && (
                            <span className="acknowledged">Acknowledged</span>
                          )}
                        </div>
                        <p>
                          {alert.camera} · {alert.detail}
                        </p>
                      </div>
                      <div className="alert-actions">
                        <span>{alert.time}</span>
                        {!alert.acknowledged && (
                          <Button onClick={() => void acknowledge(alert.id)}>
                            Acknowledge
                          </Button>
                        )}
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </section>
        </Container>
      </Box>
    </Layout>
  );
}
